/**
 * MarketDataService
 *
 * Singleton WebSocket client connecting to dextoro-market-data backend.
 * Subscribes to the global trade update channel and exposes an event API
 * for React components to receive enriched trade updates in real-time.
 *
 * Auto-reconnect with exponential backoff (same pattern as WebSocketService).
 */

import { getWsUrl } from '@/lib/tradingview/api';

export interface TradeUpdate {
  poolAddress: string;
  tokenMint: string;
  symbol: string;
  price: number;
  volume: number;
  side: 'buy' | 'sell';
  timestamp: number;
  taker: string;
  signature: string;
  priceChange5m: number | null;
  priceChange1h: number | null;
  priceChange6h: number | null;
}

type TradeUpdateCallback = (update: TradeUpdate) => void;

const MAX_RECONNECT_ATTEMPTS = 10;
const INITIAL_RECONNECT_DELAY = 1000;
const MAX_RECONNECT_DELAY = 30000;
const PING_INTERVAL = 30000;
const PAUSE_AND_RETRY_DELAY = 3 * 60 * 1000; // 3 minutes before retrying after exhausting attempts

export class MarketDataService {
  private static instance: MarketDataService | null = null;

  private ws: WebSocket | null = null;
  private listeners = new Set<TradeUpdateCallback>();
  private reconnectAttempts = 0;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private isConnecting = false;
  private shouldConnect = false;

  static getInstance(): MarketDataService {
    if (!MarketDataService.instance) {
      MarketDataService.instance = new MarketDataService();
    }
    return MarketDataService.instance;
  }

  connect(): void {
    if (this.ws || this.isConnecting) return;
    this.shouldConnect = true;
    this.doConnect();
  }

  disconnect(): void {
    this.shouldConnect = false;
    this.cleanup();
  }

  forceReconnect(): void {
    console.log('[MarketData] Force reconnect triggered');

    this.clearPing();

    // Clear any pending reconnect timeout
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    // Null out handlers before close to prevent scheduleReconnect from firing
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.onerror = null;
      try {
        this.ws.close(1000, 'Force reconnect');
      } catch { /* ignore */ }
      this.ws = null;
    }

    this.isConnecting = false;
    this.reconnectAttempts = 0;

    // Reconnect immediately if we should be connected
    if (this.shouldConnect) {
      this.doConnect();
    }
  }

  onTradeUpdate(callback: TradeUpdateCallback): () => void {
    this.listeners.add(callback);
    return () => {
      this.listeners.delete(callback);
    };
  }

  private doConnect(): void {
    if (this.isConnecting || !this.shouldConnect) return;
    this.isConnecting = true;

    try {
      const url = getWsUrl();
      this.ws = new WebSocket(url);

      this.ws.onopen = () => {
        console.log('[MarketData] Connected');
        this.isConnecting = false;
        this.reconnectAttempts = 0;

        // Subscribe to global trade updates
        this.ws?.send(JSON.stringify({ type: 'subscribe-global' }));

        // Start ping keepalive
        this.pingInterval = setInterval(() => {
          if (this.ws?.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, PING_INTERVAL);
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data as string);
          if (data.type === 'trade-update') {
            const update: TradeUpdate = {
              poolAddress: data.poolAddress,
              tokenMint: data.tokenMint,
              symbol: data.symbol,
              price: data.price,
              volume: data.volume,
              side: data.side,
              timestamp: data.timestamp,
              taker: data.taker,
              signature: data.signature,
              priceChange5m: data.priceChange5m,
              priceChange1h: data.priceChange1h,
              priceChange6h: data.priceChange6h,
            };
            for (const listener of this.listeners) {
              try {
                listener(update);
              } catch (err) {
                console.error('[MarketData] Listener error:', err);
              }
            }
          }
        } catch {
          // Ignore non-JSON messages
        }
      };

      this.ws.onclose = (event) => {
        console.log(`[MarketData] Disconnected (code: ${event.code})`);
        this.isConnecting = false;
        this.clearPing();
        this.ws = null;
        this.scheduleReconnect();
      };

      this.ws.onerror = (error) => {
        console.error('[MarketData] WebSocket error:', error);
        this.isConnecting = false;
      };
    } catch (error) {
      console.error('[MarketData] Connection error:', error);
      this.isConnecting = false;
      this.scheduleReconnect();
    }
  }

  private scheduleReconnect(): void {
    if (!this.shouldConnect) return;
    if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      console.warn('[MarketData] Max reconnect attempts reached, pausing for 3 minutes before retrying');
      setTimeout(() => {
        this.reconnectAttempts = 0;
        if (this.shouldConnect) this.doConnect();
      }, PAUSE_AND_RETRY_DELAY);
      return;
    }

    const delay = Math.min(
      INITIAL_RECONNECT_DELAY * Math.pow(2, this.reconnectAttempts),
      MAX_RECONNECT_DELAY
    );
    this.reconnectAttempts++;

    const logFn = this.reconnectAttempts <= 3 ? console.log : console.debug;
    logFn(`[MarketData] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})`);
    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      this.doConnect();
    }, delay);
  }

  private clearPing(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  private cleanup(): void {
    this.clearPing();

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      try {
        this.ws.close(1000, 'Client disconnecting');
      } catch {
        // Ignore
      }
      this.ws = null;
    }

    this.isConnecting = false;
    this.reconnectAttempts = 0;
  }
}
